import { inject, Injectable } from '@angular/core';
import { PlatformService } from '../services/platform.service';
import { ApiService } from './base-api/api.service';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CacheService } from '../services/cache.service';
import { CreateScholarshipTemplate, ScholarshipTemplate, UpdateScholarshipTemplate } from '../models/scholarship-template';

@Injectable({
  providedIn: 'root',
})
export class TemplateApiService {
  private readonly templateDraftKey = 'dheTemplateDraft';
  private readonly templatesCacheKey = 'scholarship-templates';

  private readonly platform = inject(PlatformService);
  private readonly api = inject(ApiService);
  private readonly cache = inject(CacheService);

  invalidateCache(): void {
    this.cache.invalidate(this.templatesCacheKey);
  }

  getAllScholarshipTemplates(): Observable<ScholarshipTemplate[]> {
    return this.cache.wrap(
      this.templatesCacheKey,
      this.api.get<{ data: ScholarshipTemplate[] }>('scholarship-templates').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  };

  getScholarshipTemplateById(id: number): Observable<ScholarshipTemplate> {
    return this.api.get<{ data: ScholarshipTemplate }>(`scholarship-templates/${id}`).pipe(
      map(response => response.data)
    );
  }

  saveScholarshipTemplate(template: CreateScholarshipTemplate): Observable<string> {
    return this.api.post<string>('scholarship-templates', template);
  }

  updateScholarshipTemplate(template: UpdateScholarshipTemplate): Observable<string> {
    return this.api.put<string>(`scholarship-templates/${template.id}`, template);
  }

  saveTemplateDraft(template: ScholarshipTemplate): Observable<string | undefined> {
    if (!this.platform.isBrowser) {
      return new Observable(observer => {
        observer.next(undefined);
        observer.complete();
      });
    }

    localStorage.setItem(this.templateDraftKey, JSON.stringify(template));
    return new Observable(observer => {
      observer.next('Draft saved successfully');
      observer.complete();
    });
  }

  deleteScholarshipTemplate(templateId: number): Observable<boolean> {
    return this.api.delete<{ message: boolean }>(`scholarship-templates/${templateId}`).pipe(
      map(response => response.message)
    );
  }
}